// Procedural low-poly caveman. Used for Cavelon (the pilot) and passengers.
// Returns limb references so callers can animate pedaling / waving / walking.
import {
  MeshBuilder, StandardMaterial, Color3, TransformNode, Vector3,
} from '@babylonjs/core';

function mat(scene, name, hex, emissive = 0.12) {
  const m = new StandardMaterial(name, scene);
  m.diffuseColor = Color3.FromHexString(hex);
  m.emissiveColor = m.diffuseColor.scale(emissive);
  m.specularColor = new Color3(0.05, 0.05, 0.05);
  return m;
}

// hue: 0..360 tints the fur outfit so players/passengers are distinguishable.
export function buildCaveman(scene, { hue = 25, scale = 1 } = {}) {
  const root = new TransformNode('caveman', scene);

  const skin = mat(scene, 'cm_skin', '#e0a878');
  const fur = new StandardMaterial('cm_fur', scene);
  fur.diffuseColor = Color3.FromHSV(hue, 0.55, 0.55);
  fur.emissiveColor = fur.diffuseColor.scale(0.12);
  fur.specularColor = new Color3(0.05, 0.05, 0.05);
  const hair = mat(scene, 'cm_hair', '#4a3220');

  // torso (fur tunic)
  const torso = MeshBuilder.CreateCylinder('cm_torso', { height: 0.62, diameterTop: 0.42, diameterBottom: 0.5, tessellation: 7 }, scene);
  torso.material = fur; torso.parent = root; torso.position.y = 0.62;

  // head + beard + hair tuft
  const head = new TransformNode('cm_headN', scene); head.parent = root; head.position.y = 1.1;
  const skull = MeshBuilder.CreateSphere('cm_head', { diameter: 0.42, segments: 6 }, scene);
  skull.material = skin; skull.parent = head;
  const beard = MeshBuilder.CreateSphere('cm_beard', { diameter: 0.34, segments: 5 }, scene);
  beard.material = hair; beard.parent = head; beard.position.set(0, -0.1, 0.1); beard.scaling.set(1, 0.8, 0.9);
  const tuft = MeshBuilder.CreateSphere('cm_tuft', { diameter: 0.3, segments: 5 }, scene);
  tuft.material = hair; tuft.parent = head; tuft.position.set(0, 0.16, -0.05); tuft.scaling.set(1.1, 0.6, 1.1);
  const noseM = MeshBuilder.CreateSphere('cm_nose', { diameter: 0.1, segments: 4 }, scene);
  noseM.material = skin; noseM.parent = head; noseM.position.set(0, 0, 0.2);

  // limbs — pivot at the top so rotation.x swings them naturally
  function limb(name, len, dia, x, y, material) {
    const pivot = new TransformNode(name + 'P', scene); pivot.parent = root; pivot.position.set(x, y, 0);
    const m = MeshBuilder.CreateCylinder(name, { height: len, diameter: dia, tessellation: 5 }, scene);
    m.material = material; m.parent = pivot; m.position.y = -len / 2;
    return pivot;
  }
  const armL = limb('cm_armL', 0.5, 0.13, -0.27, 0.9, skin);
  const armR = limb('cm_armR', 0.5, 0.13, 0.27, 0.9, skin);
  const legL = limb('cm_legL', 0.5, 0.16, -0.12, 0.34, skin);
  const legR = limb('cm_legR', 0.5, 0.16, 0.12, 0.34, skin);

  root.scaling.setAll(scale);
  const meshes = [torso, skull, beard, tuft, noseM,
    ...[armL, armR, legL, legR].map(p => p.getChildMeshes()).flat()];
  return { root, head, armL, armR, legL, legR, meshes };
}

// Simple walk/wave/pedal poses driven from the game loop.
// animateWave is the only pose that touches arm ROLL (rotation.z), so every
// other pose has to clear it again — otherwise a passenger who waved keeps his
// right arm sticking straight out for the rest of his life.
export function animateWalk(cm, t, speed = 1) {
  const s = Math.sin(t * 8 * speed);
  cm.legL.rotation.x = s * 0.7;
  cm.legR.rotation.x = -s * 0.7;
  cm.armL.rotation.x = -s * 0.5;
  cm.armR.rotation.x = s * 0.5;
  cm.armL.rotation.z = 0; cm.armR.rotation.z = 0;
}

// `urg` is 0 (just arrived) .. 1 (about to walk back in): an impatient customer
// waves faster, wider and hops higher, so urgency reads from the air.
export function animateWave(cm, t, urg = 0) {
  const u = Math.min(1, Math.max(0, urg));
  const sp = 1 + u * 1.1;
  cm.legL.rotation.x = 0; cm.legR.rotation.x = 0;
  cm.armL.rotation.x = 0;
  cm.armR.rotation.z = Math.PI * 0.85 + Math.sin(t * 10 * sp) * (0.35 + u * 0.3); // arm up, waving
  const hop = Math.max(0, Math.sin(t * 6 * sp));
  cm.root.position.y = (cm.baseY ?? 0) + hop * (0.25 + u * 0.18);
}

// The pedal contract: `angle` is the ABSOLUTE crank angle in radians (the caller
// integrates it, so the legs never jump when the rotor speed changes), `amp` is
// how hard he is stamping. One dial, used by both the local heli and remotes.
export function animatePedal(cm, angle, amp = 0.55) {
  cm.legL.rotation.x = 0.6 + Math.sin(angle) * amp;
  cm.legR.rotation.x = 0.6 + Math.sin(angle + Math.PI) * amp;
  cm._pedalZ = Math.sin(angle) * 0.04;   // read back by animateExhausted
  cm.root.rotation.z = cm._pedalZ;
  cm.head.rotation.z = -Math.sin(angle) * 0.03;
  cm.armL.rotation.x = cm.armR.rotation.x = -0.5 + Math.sin(angle * 0.5) * 0.06;
  cm.armL.rotation.z = 0; cm.armR.rotation.z = 0;
}

// Exhaustion + wind lean, layered ON TOP of animatePedal. Only two axes are
// touched (root pitch/roll + head pitch) so the layers never fight: the pedal
// sway is folded back in through cm._pedalZ. Call it EVERY frame — with e = 0
// it lerps the slump back out again.
export function animateExhausted(cm, e, t, sideWind = 0, dt = 0.016) {
  const k = Math.min(1, dt * 3);
  cm.root.rotation.x += (0.30 * e - cm.root.rotation.x) * k;
  cm.head.rotation.x = 0.35 * e;
  // wind lean lives on roll alone, added to the pedal sway
  const lean = -Math.max(-1, Math.min(1, sideWind / 6)) * 0.18;
  cm.root.rotation.z = (cm._pedalZ || 0) + lean;
  // gasping for air once he is really spent (baseY is set by the caller that
  // owns his seat height — without it we must not touch position at all)
  if (cm.baseY != null) cm.root.position.y = cm.baseY + (e > 0.7 ? Math.sin(t * 3.2) * 0.05 * e : 0);
}

export function animateSit(cm) {
  cm.legL.rotation.x = 1.2; cm.legR.rotation.x = 1.2;
  cm.armL.rotation.x = -0.3; cm.armR.rotation.x = -0.3;
  cm.armL.rotation.z = 0; cm.armR.rotation.z = 0;
}
